/**
 * CYP ASD Semi-Structured Interview — Configuration
 *
 * Defines the content for each DSM-5 ASD criterion assessed during the
 * developmental autism assessment for children and adolescents.
 *
 * Each criterion has two developmental stages:
 *   • Age 4–5 — historical behaviours reported by parents/carers
 *   • Current — present-day observations and reports
 *
 * Within each stage, observations are grouped into categories (e.g. "Eye contact",
 * "Conversation and reciprocity"). Each category provides:
 *   • probingQuestions — suggested interview prompts for the clinician (read-only
 *     guidance, not form fields). These are conversation starters that can reveal
 *     multiple indicators.
 *   • indicators — checkbox items the clinician ticks when the behaviour is
 *     reported or observed. These are NOT scored as "points" — each must be
 *     evaluated for pervasiveness, developmental atypicality, persistence, and
 *     whether it is better explained by other factors.
 *
 * The Functional Impairment section assesses impact across six life domains
 * (Social, Educational, Home, Emotional, Community, Self-care) using
 * multi-select checklists with evidence ratings.
 */

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

/** Evidence rating options used for each criterion and functional domain */
export const EVIDENCE_OPTIONS = [
  'Clear evidence',
  'Partial evidence',
  'No evidence',
  'Inconclusive',
];

// ---------------------------------------------------------------------------
// Criterion A1 — Social-emotional reciprocity
// ---------------------------------------------------------------------------

const CRITERION_A1 = {
  id: 'A1',
  criterionGroup: 'A',
  shortTitle: 'Social-emotional reciprocity',
  fullTitle: 'A1. Deficits in social-emotional reciprocity',
  description:
    'This criterion assesses the child/adolescent\'s ability to initiate and sustain ' +
    'reciprocal social interaction, share emotional experiences, and respond to others ' +
    'in socially expected ways appropriate to their developmental level. It includes ' +
    'motivation to engage, intuitive responsiveness, and comfort with age-appropriate ' +
    'social dynamics.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'conversation',
          title: 'Conversation and communication',
          probingQuestions: [
            'When [child/young person] was little, how did they communicate with you before they could talk? Did they babble back when you spoke to them?',
            'Did they point to show you things they found interesting, or just point to ask for things they wanted?',
            'How did they respond when you smiled at them or tried to play peek-a-boo?',
            'Would they bring you things just to show you, or only when they needed help?',
            'When they were upset, did they seek comfort from you? How did they show they wanted comfort?',
          ],
          indicators: [
            'Limited or absent initiation of social interaction with parents/carers or peers',
            'Inconsistent or reduced response to name by 12 months',
            'Rarely pointed to share interest',
            'Pointed mainly to request, not to share',
            'Limited range of facial expressions during interaction',
            'Did not imitate others\' actions or sounds',
          ],
        },
        {
          id: 'emotionalSharing',
          title: 'Emotional sharing',
          probingQuestions: [
            'Did they ever look at you to share enjoyment — like after doing something they were proud of?',
          ],
          indicators: [
            'Preferred solitary play; rejected or ignored others\' attempts to join',
            'Difficulty engaging in reciprocal back-and-forth babbling/vocalisation',
          ],
        },
        {
          id: 'imitation',
          title: 'Imitation',
          probingQuestions: [
            'Would they copy your actions, like waving or clapping, without being prompted?',
          ],
          indicators: [
            'Limited response to smiling, peek-a-boo, or playful interaction',
            'Did not consistently seek comfort when hurt or distressed',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'conversationReciprocity',
          title: 'Conversation and reciprocity',
          probingQuestions: [
            'How do conversations feel with [child/young person]? Is it easy to have a back-and-forth exchange?',
            'Do they ask you questions about your day, your thoughts, or your feelings?',
            'What happens if you interrupt them or try to add information to what they\'re saying? Do they acknowledge your contribution, or ignore it and continue?',
            'How does [child/young person] manage conversations with peers?',
            'Do they ever hide their social difficulties to fit in? What does this look like?',
            'Do they share their thoughts, feelings, and experiences with you or others spontaneously?',
            'Do you feel you have a sense of who [child/young person] is internally — their worries, hopes, private thoughts?',
          ],
          indicators: [
            'Conversations feel one-sided; child/young person talks at others rather than with them',
            'Conversations feel scripted, effortful, or lacking in spontaneous exchange',
            'Difficulty with conversational turn-taking',
            'Difficulty with the back-and-forth of social banter or informal talk',
            'Does not ask about others\' experiences, thoughts, or feelings',
            'Changes topic abruptly or fails to build on what others have said',
            'Responds to questions but does not expand or elaborate',
            'Ignores or dismisses others\' contributions to conversation',
            'Difficulty maintaining conversations with peers',
            'Appears disinterested in others or self-focused',
            'Seems to consciously copy peers\' behaviour, dress, or speech to blend in',
            'Seems unaware of how others perceive them',
            'Limited sharing of inner world; parents feel they "don\'t know what\'s going on" with them',
          ],
        },
        {
          id: 'emotionalReciprocity',
          title: 'Emotional reciprocity',
          probingQuestions: [
            'If you\'re feeling sad, upset, or unwell, does [child/young person] notice? Do they ever try to comfort you or ask what\'s wrong?',
            'When something exciting happens for them, do they seek you out to share it?',
            'How do they respond when another child/young person is upset?',
          ],
          indicators: [
            'Rarely shares achievements, excitement, or concerns spontaneously',
            'Does not typically notice when others are distressed',
            'Offers comfort but in a scripted or mismatched way',
            'Limited sharing of enjoyment or joint enthusiasm',
            'Responses to others\' emotions appear delayed, intellectualised, or absent',
          ],
        },
        {
          id: 'socialInitiation',
          title: 'Social initiation',
          probingQuestions: [
            'Does [child/young person] approach other children to play?',
            'How do they join in when other children are already playing?',
          ],
          indicators: [
            'Rarely initiates interaction with peers',
            'Does not know how to join group play or conversations',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Criterion A2 — Nonverbal communication
// ---------------------------------------------------------------------------

const CRITERION_A2 = {
  id: 'A2',
  criterionGroup: 'A',
  shortTitle: 'Nonverbal communication',
  fullTitle: 'A2. Deficits in nonverbal communicative behaviours used for social interaction',
  description:
    'This criterion assesses the child/adolescent\'s integration and use of nonverbal ' +
    'communication to regulate social interaction — including eye contact, facial expression, ' +
    'gestures, body posture, and prosody (rhythm and tone of speech). These differences may ' +
    'affect how communication is interpreted by others and may lead to misunderstandings or ' +
    'perceived social awkwardness.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'eyeContact',
          title: 'Eye contact',
          probingQuestions: [
            'What do you remember about [child/young person]\'s eye contact when they were very young? Did they look at you when feeding, playing, or when you spoke to them?',
            'Did they look at people\'s faces when interacting, or did they seem to look past people?',
            'Was their eye contact something you ever wondered about or others commented on?',
            'Did it feel like they were not really looking at you, even when facing you?',
            'Did they sometimes stare at people in a way that felt unusual or uncomfortable?',
          ],
          indicators: [
            'Avoided or limited eye contact from early infancy',
            'Looked at mouth, hands, or objects rather than eyes during interaction',
            'Eye contact described as fleeting, inconsistent, or absent',
            'Stared intensely or too long without understanding social context',
            'Made eye contact only with familiar adults, not peers',
            'Gaze described as "blank," "distant," or "through you"',
          ],
        },
        {
          id: 'facialExpression',
          title: 'Facial expression',
          probingQuestions: [
            'Could you tell what [child/young person] was feeling from their face, or did you rely more on sounds or behaviour?',
            'Did they show a range of facial expressions appropriate to situations?',
          ],
          indicators: [
            'Facial expressions appeared flat, limited, or difficult to read',
            'Expressions mismatched with context (e.g., laughing when others distressed)',
            'Range of expressions limited compared to same-age peers',
          ],
        },
        {
          id: 'gestures',
          title: 'Gestures',
          probingQuestions: [
            'Before they could talk, how did [child/young person] communicate what they wanted? Did they use gestures like pointing, reaching, waving?',
            'Did they wave goodbye spontaneously, or only when prompted?',
            'Would they nod or shake their head to answer questions?',
            'Did they use your hand as a tool — placing your hand on objects they wanted rather than pointing or asking?',
            'If you pointed to something across the room and said \'look\', would they follow your point with their eyes, or would they just look at your finger?',
            'Did they ever use gestures, other than pointing or holding arms up to be lifted, to let you know what they wanted?',
          ],
          indicators: [
            'Limited or absent use of pointing to share interest',
            'Pointed to request but not to share',
            'Did not wave, clap, or use other social gestures spontaneously',
            'Used adult\'s hand as a tool (placed hand on object) rather than gesturing',
            'Limited use of gestures',
            'Looked at adult\'s finger rather than following the point to the object',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'eyeContactCurrent',
          title: 'Eye contact',
          probingQuestions: [
            'How does [young person] manage eye contact now? Does it feel natural, or have you noticed anything different about it?',
            'Do they notice if others are trying to catch their eye, or use eye contact to communicate interest or disinterest?',
          ],
          indicators: [
            'Eye contact remains inconsistent, or fleeting',
            'Makes eye contact but it looks uncomfortable or effortful for them',
            'Difficulty using eye contact to show interest',
            'Stares in ways that peers find uncomfortable or odd',
            'Difficulty using eye contact to regulate conversation (e.g., looking to signal turn-taking)',
          ],
        },
        {
          id: 'facialGestureCurrent',
          title: 'Facial expression and gesture',
          probingQuestions: [
            'Can you usually tell what [child/young person] is feeling from their face, or do you need to ask?',
            'Do they use their hands when they talk — gestures that add to what they\'re saying?',
            'Do they ever use other common gestures, such as blowing a kiss, clapping for a job well done, putting a finger to their lips meaning to "be quiet", or shaking a finger for "bad"?',
          ],
          indicators: [
            'Facial expressions still appear limited, exaggerated, or mismatched',
            'Facial expressions seem subtle, flat, or difficult for others to read',
            'Gestures are limited, unusual, or not integrated with speech',
            'Uses gestures but misses subtlety (e.g., pointing without shared gaze)',
          ],
        },
        {
          id: 'readingOthers',
          title: 'Reading others',
          probingQuestions: [
            'How well does [child/young person] read other people\'s nonverbal cues? For example, can they tell when someone is bored, annoyed, or upset without being told?',
            'Do they sometimes think you\'re angry when you\'re not, based on your tone or face?',
          ],
          indicators: [
            'Frequently misses subtle social cues from peers',
            'Does not notice when others are losing interest in conversation',
            'Misinterprets tone of voice, leading to confusion or conflict',
            'Difficulty reading others\' facial expressions (e.g., seems not to notice when someone is bored, upset, or annoyed)',
            'Misinterprets neutral expressions as anger or disapproval',
            'Relies on explicit communication rather than nonverbal cues',
            'Needs explicit verbal explanation to understand how others feel',
          ],
        },
        {
          id: 'toneProsody',
          title: 'Tone and prosody',
          probingQuestions: [
            'Has anyone ever commented on [child/young person]\'s tone of voice — like that it\'s unusually loud, quiet, monotone, sing-song or doesn\'t match the situation?',
            'Do they adjust their voice to suit different situations (quieter in library, more animated with friends)?',
          ],
          indicators: [
            'Tone perceived sarcastic when not intended',
            'Tone described as flat, monotone, overly formal, or unusual in rhythm',
            'Volume may be too loud or too soft for context',
            'Difficulty using tone to convey emotion or emphasis',
            'Difficulty modulating tone for different social contexts',
          ],
        },
        {
          id: 'bodyLanguage',
          title: 'Body language and personal space',
          probingQuestions: [
            'Does [child/young person] seem aware of personal space — theirs and others\'? Do they stand too close or too far when talking?',
            'Have you noticed anything different about how they hold their body when interacting?',
            'Do they notice and interpret others\' body language accurately?',
          ],
          indicators: [
            'Difficulty with personal space — stands too close or too distant',
            'Body language appears stiff, awkward, or mismatched',
            'Posture does not align with conversational context',
            'Misreads others\' body language, leading to social errors',
          ],
        },
        {
          id: 'masking',
          title: 'Masking',
          probingQuestions: [
            'Do they consciously work to control their facial expressions, eye contact, or body language to fit in?',
          ],
          indicators: [
            'Consciously monitors and adjusts nonverbal behaviour to fit in',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Criterion B1 — Stereotyped or repetitive behaviour / speech
// ---------------------------------------------------------------------------

const CRITERION_B1 = {
  id: 'B1',
  criterionGroup: 'B',
  shortTitle: 'Stereotyped behaviour/speech',
  fullTitle: 'B1. Stereotyped or repetitive motor movements, use of objects, or speech',
  description:
    'This criterion assesses the presence of repetitive motor movements (e.g. hand-flapping, ' +
    'rocking, toe-walking), non-functional use of objects (e.g. lining up, spinning), and ' +
    'stereotyped or repetitive speech (e.g. echolalia, scripted language). These behaviours may ' +
    'serve a self-regulatory function and can change in form over the developmental period — ' +
    'becoming more subtle, internalised, or consciously suppressed in older children and adolescents.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'motorStereotypies',
          title: 'Motor stereotypies',
          probingQuestions: [
            'When [child/young person] was little, did they have any repeated movements — like hand-flapping, spinning, rocking, or toe-walking?',
            'When did you notice these? What seemed to trigger them — excitement, distress, concentration?',
            'Did anyone comment on these movements, or were you ever concerned?',
            'Did they seem to do these movements because they enjoyed the feeling, or more automatically when excited or upset?',
          ],
          indicators: [
            'Hand-flapping, finger-flicking, or hand-posturing (especially when excited or distressed)',
            'Spinning self or objects repeatedly',
            'Rocking back and forth',
            'Toe-walking',
            'Pacing in repetitive patterns',
            'Jumping or bouncing repetitively',
          ],
        },
        {
          id: 'objectUse',
          title: 'Object use',
          probingQuestions: [
            'How did they play with toys? Did they use them as intended, or were they more interested in certain parts (like spinning wheels)?',
            'Did they line up objects, spin things, or become fascinated with specific parts of toys?',
            'Were there particular objects they carried everywhere or showed intense attachment to?',
          ],
          indicators: [
            'Lined up toys or objects rather than playing with them imaginatively',
            'Focused on parts of objects (e.g., spinning wheels, opening/closing doors) rather than whole toy',
            'Repeatedly dropped, banged, or threw objects',
            'Opened and closed doors or drawers repetitively',
            'Fascinated with spinning objects (e.g., fans, wheels)',
            'Strong attachment to unusual objects (e.g., piece of string, stone)',
          ],
        },
        {
          id: 'repetitiveSpeech',
          title: 'Repetitive speech',
          probingQuestions: [
            'Did they repeat words or phrases they heard, either straight away or later?',
            'Did they have favourite phrases they repeated?',
          ],
          indicators: [
            'Immediate echolalia (repeating what was just said)',
            'Delayed echolalia (repeating phrases from TV, books, or conversations heard earlier)',
            'Repeated scripted phrases from movies/books',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'motorStereotypiesCurrent',
          title: 'Motor stereotypies',
          probingQuestions: [
            'Does [child/young person] still have any repetitive movements now? Have they changed in appearance or been suppressed?',
            'Do they seem to hide or control these movements in public?',
            'Do they have any \'stims\' they use to self-regulate when anxious, excited, or overwhelmed?',
          ],
          indicators: [
            'Movements seem to be more subtle, controlled, or hidden now',
            'Engages in stereotypies when excited, stressed, or alone',
            'Fidgeting, tapping, or repetitive movements present but less obvious',
            'Engages in stereotypies only when alone or very stressed',
            'Complex body mannerisms and hand/finger mannerisms (e.g. flapping, bouncing, spinning, toe-walking, clicking joints, tensing arms, flicking fingers, rocking, eye squeezing, throat clearing)',
            'Uses stimming as a coping strategy for sensory or emotional regulation',
            'Consciously suppresses repetitive movements in public',
            'Uses fidget toys or other tools as a socially acceptable alternative',
            'Suppressing repetitive movements in public leads to exhaustion or increased anxiety',
          ],
        },
        {
          id: 'objectUseCurrent',
          title: 'Object use',
          probingQuestions: [
            'Are there any objects they collect, arrange, or interact with in a repetitive way?',
            'Do they have rituals around how things should be arranged in their room or belongings?',
          ],
          indicators: [
            'Collects or arranges objects in specific ways',
            'Strong preference for specific objects to be in exact positions',
            'Has strict organisation systems for belongings',
            'Engages in repetitive activities (e.g., building same structure repeatedly)',
            'Rituals around studying, screen time, or daily routines',
          ],
        },
        {
          id: 'repetitiveSpeechCurrent',
          title: 'Repetitive speech and language patterns',
          probingQuestions: [
            'Do they ever repeat phrases or scripts from movies, TV, or online content in conversation?',
            'Are there any unusual patterns in their speech — like repeating phrases, using very formal language, or having a distinctive rhythm?',
          ],
          indicators: [
            'Still uses scripted language, especially when anxious or uncertain',
            'Speech is unusually formal, pedantic, or has unusual prosody',
            'Uses scripted language from movies, books, or online content',
            'Uses formal or inappropriate language which they don\'t understand (incorrect use of words/phrases)',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Criterion B2 — Insistence on sameness / routines
// ---------------------------------------------------------------------------

const CRITERION_B2 = {
  id: 'B2',
  criterionGroup: 'B',
  shortTitle: 'Insistence on sameness',
  fullTitle: 'B2. Insistence on sameness, inflexible adherence to routines, or ritualised patterns',
  description:
    'This criterion assesses the child/adolescent\'s need for predictability and resistance to ' +
    'change. It covers rigid adherence to routines, ritualised behaviours, distress at transitions ' +
    'or unexpected changes, and inflexible thinking patterns. These features may manifest as ' +
    'difficulty coping with everyday variation and can significantly impact independence, family ' +
    'life, and educational participation.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'routinesRituals',
          title: 'Routines and rituals',
          probingQuestions: [
            'When [child/young person] was little, were there routines or rituals that were really important to them?',
            'How did they react if things weren\'t done the usual way — like taking a different route to nursery, or using a different cup?',
            'Were there particular foods they would only eat prepared in a specific way?',
            'Did they have little rituals they had to do in a certain order?',
            'Were they very particular about where their belongings were kept? Did they notice immediately if something had been moved or touched?',
            'How did they cope with unexpected events like someone arriving unannounced, a sudden change of plan, or something breaking?',
            'Did they ask the same question repeatedly, needing the exact same answer? Did they correct you if you phrased something differently than expected?',
            'Were there rigid rules about mealtimes like specific cutlery, specific plate, order of eating, or how food was presented?',
          ],
          indicators: [
            'Strong preferences for sameness in daily routines (same food, same route, same order of activities)',
            'Distress if routine was disrupted — meltdowns, crying, extreme upset',
            'Needed things "just so" (e.g., food not touching on plate, specific cup/plate)',
            'Insisted on same bedtime story, same bath time routine',
            'Insisted on specific greeting or goodnight rituals (e.g., exact words, order of hugs/kisses)',
            'Rigid about clothing — would only wear certain items',
            'Became distressed if furniture was moved or room layout changed',
            'Became distressed by unexpected changes (e.g., someone sitting in their usual seat)',
            'Extreme distress if belongings were moved or touched by others',
            'Insisted on doing activities in a specific order (e.g., lining up shoes before leaving, arranging toys before bed)',
            'Extreme distress or inability to cope with unexpected changes',
            'Repeated questioning requiring exact same answer; corrected if phrasing changed',
            'Rigid rules about mealtime presentation (specific plate/cutlery, food in specific position)',
          ],
        },
        {
          id: 'transitions',
          title: 'Transitions',
          probingQuestions: [
            'How did they manage moving from one activity to another, leaving the house, or going to bed?',
            'Did they need a lot of warning before changes?',
          ],
          indicators: [
            'Significant distress during transitions between activities',
            'Needed extended preparation or warnings before changes',
            'Difficulty stopping a preferred activity to move to something else',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'routinesRitualsCurrent',
          title: 'Routines and rituals',
          probingQuestions: [
            'What routines are important to [child/young person] now? How do they react if these are disrupted?',
            'Do they have rigid rules about how things should be done — at home, at school, or with friends?',
            'How do they handle planned changes (like holidays, school breaks) versus unexpected changes (like a last-minute appointment)?',
          ],
          indicators: [
            'Rigid adherence to routines (same morning routine, same after-school schedule, same foods)',
            'Significant distress, anxiety, or dysregulation when routines disrupted',
            'Tries to impose routines on others',
            'Strong preferences for particular foods, clothing, or arrangements',
            'Has rituals around homework, bedtime, or screen time',
            'Difficulty adapting to changing demands of school or college (different teachers, rooms, schedules)',
          ],
        },
        {
          id: 'flexibilityAdaptability',
          title: 'Flexibility and adaptability',
          probingQuestions: [
            'Does [child/young person] get \'stuck\' on ideas — like once they\'ve decided something, it\'s very hard to change their mind?',
            'How do they cope when things don\'t go according to plan?',
          ],
          indicators: [
            'Needs significant preparation and warning for changes',
            'Has severe anxiety about upcoming changes (school trips, school changes, supply teachers, non-uniform day, going on holiday)',
            'Difficulty with spontaneous plans or surprises',
            'Transition between home and school, between weekend and school week, winter/summer, or school holiday and return to school is challenging',
          ],
        },
        {
          id: 'rigidThinking',
          title: 'Rigid thinking patterns',
          probingQuestions: [
            'Does [child/young person] tend to see things in black and white, with little middle ground?',
            'How do they handle being wrong?',
            'Do they have difficulty with concepts like \'sometimes\' or \'maybe\' — preferring definite answers?',
            'How do they handle different opinions, perspectives, or criticism?',
            'Do they get \'stuck\' on rules or principles, even when flexibility would be helpful?',
          ],
          indicators: [
            'Difficulty seeing others\' perspectives',
            'Holds grudges or has rigid sense of fairness',
            'Gets "stuck" on rules and struggles when exceptions occur',
            'Difficulty with ambiguous situations or answers',
            'Insists on rules being followed by everyone',
            'Difficulty seeing shades of grey; thinking is dichotomous (all good/all bad)',
            'Holds rigid moral or ethical views and struggles when others don\'t conform',
            'Difficulty accepting different perspectives',
            'Ruminates or gets "stuck" on perceived injustices',
          ],
        },
        {
          id: 'impactIndependence',
          title: 'Impact on independence',
          probingQuestions: [
            'How does their need for routine affect their independence — managing their own time, homework, or daily activities?',
            'Do they struggle with the unpredictability of social situations, part-time work, or future planning?',
          ],
          indicators: [
            'Relies on routines to manage daily activities; struggles without structure',
            'Difficulty with open-ended tasks or independent planning',
            'Anxiety about future (college, work, relationships) due to unpredictability',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Criterion A3 — Relationships
// ---------------------------------------------------------------------------

const CRITERION_A3 = {
  id: 'A3',
  criterionGroup: 'A',
  shortTitle: 'Relationships',
  fullTitle: 'A3. Deficits in developing, maintaining, and understanding relationships',
  description:
    'This criterion assesses the child/adolescent\'s ability to develop, maintain, and understand ' +
    'relationships appropriate to their developmental level. It encompasses interest in peers, ' +
    'quality of friendships, ability to navigate group dynamics and social hierarchies, understanding ' +
    'of social concepts like loyalty and reciprocity, social motivation, and adaptation of behaviour ' +
    'across different social contexts.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'interestInPeers',
          title: 'Interest in peers',
          probingQuestions: [
            'When [child/young person] was little, how did they respond to other children? Were they interested, indifferent, or distressed?',
            'At nursery or playgroup, did they play alongside others or try to join in?',
            'Did they have a particular friend or preferred playmate?',
          ],
          indicators: [
            'Little or no interest in other children; preferred adults or solitary play',
            'Watched other children but did not attempt to join play',
            'Became distressed when other children approached or tried to play',
            'Engaged in parallel play but did not progress to interactive play',
            'Preferred to play alone rather than with others',
            'Showed more interest in adults than same-age peers',
          ],
        },
        {
          id: 'playSkills',
          title: 'Play skills',
          probingQuestions: [
            'What kind of play did they enjoy — imaginative play, physical play, or more solitary activities like puzzles or drawing?',
            'If they engaged in pretend play, did they include others, or was it mostly solitary?',
            'How did they manage sharing and turn-taking with other children?',
          ],
          indicators: [
            'Limited or absent imaginative or pretend play',
            'Play was repetitive rather than creative or imaginative',
            'Difficulty sharing, taking turns, or following simple game rules',
            'Became upset if others didn\'t follow their play "rules"',
            'Preferred sensory or construction play over social play',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'friendships',
          title: 'Friendships',
          probingQuestions: [
            'Does [child/young person] have friends? How would you describe these friendships?',
            'How do these friendships develop — do they initiate, or do other children befriend them?',
            'Do they have a best friend or someone they confide in? What does that friendship look like?',
            'What do they do when they\'re with friends? Is it interactive, or more side-by-side activities?',
            'How do they handle disagreements or conflicts with friends?',
          ],
          indicators: [
            'Few or no reciprocal friendships',
            'Friendships are often based on shared interests rather than emotional connection',
            'Has acquaintances but not close friends',
            'Prefers younger or older children; struggles with same-age peer interactions',
            'Perceived as "different," "immature," or "socially awkward" by peers',
          ],
        },
        {
          id: 'groupSituations',
          title: 'Group situations',
          probingQuestions: [
            'How does [child/young person] manage in group situations — like at birthday parties, clubs, or group activities?',
            'At social settings, what do they typically do? Play with others, wander, or seek out adults?',
            'Do they seem to understand the unwritten rules of the playground — who plays with whom, how to join games?',
            'How do they handle peer groups — do they have a group they belong to, or do they move between groups?',
            'Are they aware of social dynamics like cliques, popularity, and reputation?',
          ],
          indicators: [
            'Difficulty joining ongoing peer activities',
            'Tries to join play in awkward or inappropriate ways',
            'Often plays alone at break times or seeks out adults',
            'Excluded, ignored, or bullied by peers',
            'Difficulty navigating the unwritten rules of social hierarchies',
          ],
        },
        {
          id: 'socialUnderstanding',
          title: 'Social understanding',
          probingQuestions: [
            'Does [child/young person] seem to understand what friendship means — loyalty, sharing secrets, mutual support?',
            'How do they react when a friendship ends or a friend plays with someone else?',
            'Do they adapt their behaviour appropriately with different people (teachers, peers, family, strangers)?',
            'How well does [child/young person] understand others\' perspectives — in friendships, family, or conflicts?',
          ],
          indicators: [
            'Confused about what defines a friend versus acquaintance',
            'Rigid expectations of how friendships "should" work',
            'Difficulty understanding others\' perspectives, leading to conflicts',
            'Holds grudges or has rigid thinking about fairness',
            'Does not adapt behaviour appropriately across different social contexts (e.g., same with teachers as with friends)',
            'Takes things literally in social situations, missing sarcasm or implied meaning',
          ],
        },
        {
          id: 'socialMotivation',
          title: 'Social motivation',
          probingQuestions: [
            'Does [child/young person] want friends but struggle to make them, or do they seem content without peer relationships?',
            'Do they ever express feeling lonely or left out?',
            'Do they try to hide their social difficulties or copy others to fit in?',
          ],
          indicators: [
            'Expresses desire for friends but doesn\'t know how to make or keep them',
            'Seems content with solitary activities and not seeking peer interaction',
            'Has reported feeling lonely, left out, or different from peers',
            'Works hard to fit in, leading to exhaustion and burnout',
          ],
        },
        {
          id: 'onlineOffline',
          title: 'Online vs offline relationships',
          probingQuestions: [
            'Do they have online friendships? How do online relationships compare to in-person ones?',
          ],
          indicators: [
            'Prefers online friendships as social rules seem clearer and interaction is more controlled',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Criterion B3 — Restricted interests
// ---------------------------------------------------------------------------

const CRITERION_B3 = {
  id: 'B3',
  criterionGroup: 'B',
  shortTitle: 'Restricted interests',
  fullTitle: 'B3. Highly restricted, fixated interests that are abnormal in intensity or focus',
  description:
    'This criterion assesses whether the child/adolescent has interests that are unusually intense, ' +
    'narrow, or all-consuming relative to developmental expectations. The interest itself may or ' +
    'may not be unusual — what matters is the degree of fixation, the amount of time devoted to it, ' +
    'and whether it interferes with other activities, relationships, or daily functioning. These ' +
    'interests may also serve an important self-regulatory function and form a core part of identity.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'earlyInterests',
          title: 'Early interests',
          probingQuestions: [
            'When [child/young person] was little, were there particular things they were fascinated by — more than other children their age?',
            'What did that interest look like? How much time did they spend engaged with it?',
            'Did they want to talk about it, look at books about it, watch videos, collect things?',
            'How did they react if you tried to redirect them to something else?',
            'Did this interest interfere with other activities like joining in family events, playing with other children, or trying new things?',
          ],
          indicators: [
            'Intense, narrow interests unusual for their age (e.g., vacuum cleaners, washing machines, street signs, numbers, letters)',
            'Would focus on one topic or object for extended periods',
            'Wanted books read only about that topic',
            'Talked about their interest constantly',
            'Collected items related to their interest',
            'Became highly distressed if interest was interrupted or unavailable',
            'Their interest seemed unusual or "odd" to others',
            'Interest prevented or significantly limited participation in other activities',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'currentInterests',
          title: 'Current interests',
          probingQuestions: [
            'What are [child/young person]\'s main interests or passions now?',
            'How much time do they spend on this interest? Does it interfere with other activities?',
            'Do they prefer to engage with this interest alone, or do they try to involve others?',
            'Has the interest changed over time, or does it follow a similar pattern of intensity?',
            'Do they use their interest to self-regulate — to calm down when anxious or overwhelmed?',
          ],
          indicators: [
            'Has one or more intense interests that dominate conversation and free time',
            'Has intense interests that may be more socially acceptable but still all-consuming (e.g., specific video games, anime, fandoms, music genres, historical periods, scientific topics)',
            'Accumulates extensive knowledge; considered an "expert" in their chosen area of interest',
            'Has encyclopaedic knowledge of their interest',
            'Difficulty engaging in conversation about other topics',
            'Talks about the interest regardless of listener\'s interest',
            'Uses interest to self-regulate (calms self through engagement with interest)',
            'Resists or becomes distressed when prevented from engaging with interest',
            'Interests may shift but follow same "all-consuming" pattern',
            'Spends majority of free time engaged with their interest',
          ],
        },
        {
          id: 'socialAspects',
          title: 'Social aspects',
          probingQuestions: [
            'Do they use this interest to connect with others (online or in-person)?',
            'How do peers respond to their intense interest?',
          ],
          indicators: [
            'Connects with others who share the interest, but interaction remains focused on the topic',
            'Teased or excluded due to intense or unusual interests',
            'Learnt to hide or downplay interests to fit in',
            'Interest dominates conversations; struggles to engage in other topics',
          ],
        },
        {
          id: 'onlineCommunities',
          title: 'Online communities',
          probingQuestions: [
            'Are they part of online communities or fandoms related to their interests?',
          ],
          indicators: [
            'Finds community and friendship through shared interests online',
            'Prefers online friendships around interests to in-person relationships',
          ],
        },
        {
          id: 'identitySelfRegulation',
          title: 'Identity and self-regulation',
          probingQuestions: [
            'Does this interest form a big part of their identity — how they see themselves?',
            'What do they regard as their special skills and strengths?',
          ],
          indicators: [
            'Interest forms a core part of identity and self-concept',
            'Struggles with self-worth if interest is not validated by others',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Criterion B4 — Sensory reactivity
// ---------------------------------------------------------------------------

const CRITERION_B4 = {
  id: 'B4',
  criterionGroup: 'B',
  shortTitle: 'Sensory reactivity',
  fullTitle: 'B4. Hyper- or hyporeactivity to sensory input or unusual sensory interests',
  description:
    'This criterion assesses the child/adolescent\'s sensory processing across multiple modalities ' +
    '(auditory, tactile, oral/olfactory, visual, vestibular/proprioceptive) and interoception. ' +
    'It covers both hypersensitivity (over-responsiveness leading to avoidance or distress) and ' +
    'hyposensitivity (under-responsiveness or sensory seeking). Sensory differences may change in ' +
    'presentation over time and can significantly affect daily functioning, food intake, clothing ' +
    'choices, environmental tolerance, and emotional regulation.',
  available: true,
  stages: [
    {
      id: 'early',
      title: 'Age 4–5',
      categories: [
        {
          id: 'auditory',
          title: 'Auditory sensitivities',
          probingQuestions: [
            'Was [child/young person] sensitive to sounds as a baby or toddler — like covering ears to vacuum, hair dryer, sirens, or loud noises?',
            'Did they startle easily or seem bothered by noises that didn\'t bother other children?',
          ],
          indicators: [
            'Covered ears to specific sounds (vacuum, hand dryer, sirens, crying)',
            'Distressed by multiple sounds simultaneously (noisy environments)',
            'Difficulty sleeping due to environmental noises',
            'Avoided environments (e.g., playgrounds, parties, shops) due to sensory overload',
          ],
        },
        {
          id: 'tactile',
          title: 'Tactile sensitivities',
          probingQuestions: [
            'How did they respond to touch — did they enjoy cuddling, or seem uncomfortable being held?',
            'Were there issues with hair washing, nail cutting, tooth brushing, or face washing?',
            'Were they picky about clothing — certain textures, tags, seams?',
          ],
          indicators: [
            'Avoided certain clothing textures, tags, seams, or fabrics',
            'Distressed by hair washing, brushing, or nail cutting',
            'Difficulty with nail cutting or tooth brushing',
            'Avoided getting hands messy (sand, paint, glue, food)',
            'Disliked bare feet on certain surfaces',
            'Resisted cuddling or physical affection',
          ],
        },
        {
          id: 'oralOlfactory',
          title: 'Oral and olfactory sensitivities',
          probingQuestions: [
            'Were they a picky eater, especially with textures, taste, temperatures, or smells?',
            'Were they sensitive to smells, bothered by cooking smells, perfumes, or certain foods, or did they seem to seek out smells by sniffing objects or people?',
          ],
          indicators: [
            'Extremely limited food preferences based on texture, not just taste',
            'Difficulty with mixed textures or taste',
            'Gagged easily on certain food textures or taste',
            'Bothered by smells others didn\'t notice (cooking, perfume, cleaning products)',
            'Sought out smells by sniffing objects or people',
          ],
        },
        {
          id: 'visual',
          title: 'Visual sensitivities',
          probingQuestions: [
            'Did they seem bothered by bright lights or prefer dim lighting?',
          ],
          indicators: [
            'Bothered by bright lights or sunlight',
            'Preferred dim lighting',
          ],
        },
        {
          id: 'vestibularProprioceptive',
          title: 'Vestibular/proprioceptive',
          probingQuestions: [
            'Did they seek out intense movement — spinning, swinging, jumping, climbing?',
            'Or did they avoid movement — hesitant on swings, slides, or climbing?',
          ],
          indicators: [
            'Avoided movement (hesitant on swings, slides, climbing)',
            'Became anxious with feet off ground',
            'Sought intense movement — spinning, swinging, jumping, climbing',
            'Sought intense movement or pressure in ways that created safety risks (e.g., climbing high furniture, crashing into objects)',
          ],
        },
        {
          id: 'hyposensitivities',
          title: 'Hyposensitivities (under-responsive)',
          probingQuestions: [
            'Did they seem unaware of pain, temperature, or injury?',
          ],
          indicators: [
            'High pain tolerance — didn\'t notice injuries',
            'Unaware of temperature (would go outside without coat)',
            'Didn\'t notice when hungry, thirsty, or needing bathroom',
          ],
        },
        {
          id: 'sensorySeeking',
          title: 'Sensory seeking',
          probingQuestions: [
            'Did they seek out intense sensory input like crashing, bumping, deep pressure?',
            'Did they seem fascinated by certain sensory experiences like watching spinning objects, looking at lights, sniffing things?',
          ],
          indicators: [
            'Craved intense movement like spinning, jumping, crashing',
            'Sought deep pressure like tight hugs, weighted blankets, squeezing into tight spaces',
            'Fascinated with spinning objects, lights, reflections',
            'Sniffed objects, people, or food inappropriately',
            'Chewed on non-food items (clothing, toys, pencils)',
          ],
        },
      ],
    },
    {
      id: 'current',
      title: 'Current',
      categories: [
        {
          id: 'currentSensitivities',
          title: 'Current sensitivities',
          probingQuestions: [
            'What sensory sensitivities does [child/young person] have now? Have they changed over time and how do they manage these day-to-day?',
            'How do they cope with sensory demands of environments like school corridors, assemblies, supermarkets?',
            'Do they use any strategies to manage sensory input — headphones, fidgets, specific clothing?',
            'Do they avoid certain situations because of sensory overload?',
          ],
          indicators: [
            'Still distressed by certain sounds',
            'Uses headphones, ear defenders, or other tools to manage sound sensitivity',
            'Avoids certain foods based on texture or taste',
            'Food choices remain limited due to texture/taste sensitivities',
            'Chooses clothing based on comfort, not appearance',
            'Avoids certain environments (cafeterias, parties, crowded spaces) due to sensory overload',
            'Does not notice pain or injury',
          ],
        },
        {
          id: 'sensorySeekingRegulation',
          title: 'Sensory seeking and regulation',
          probingQuestions: [
            'Do they seek out certain sensory experiences (music, movement, pressure) to regulate?',
            'What do they do when they\'re overwhelmed or need to calm down?',
          ],
          indicators: [
            'Seeks intense movement or proprioceptive input',
            'Uses music, exercise, or stimming to regulate emotions',
            'Seeks deep pressure (weighted blankets, tight hugs) or movement',
            'Engages in stimming but conceals it in public',
          ],
        },
        {
          id: 'interoception',
          title: 'Interoception',
          probingQuestions: [
            'Does [child/young person] notice internal body signals like hunger, thirst, temperature, pain, needing the toilet?',
            'Do they recognise when they\'re feeling anxious or overwhelmed, or does it seem to come out of nowhere?',
            'Can they identify and name their emotions, or do emotions show up as physical sensations (headaches, stomach aches)?',
          ],
          indicators: [
            'Difficulty recognising hunger, thirst, fullness',
            'Does not notice needing bathroom until urgent',
            'Difficulty identifying emotions — describes it as physical sensations (stomach ache when anxious)',
          ],
        },
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Functional Impairment domains
// ---------------------------------------------------------------------------

export const FUNCTIONAL_IMPAIRMENT_LEAD_QUESTION =
  'Thinking about everything we\'ve discussed, how do they affect [child/young person]\'s ' +
  'day-to-day life? Can you tell me about any difficulties, stress, or limitations they ' +
  'experience at home, at school, with friends, or in the community because of these differences?';

export const FUNCTIONAL_IMPAIRMENT_DOMAINS = [
  {
    id: 'social',
    title: 'Social',
    indicators: [
      'Struggles to make or keep friends',
      'Friendships are often short-lived or end due to misunderstandings',
      'Misunderstandings leading to conflict or withdrawal',
      'Plays alone even when wanting to join in',
      'Difficulty sharing feelings or connecting emotionally',
      'Being left out, isolated, or bullied',
      'Strain in sibling and/or parental relationships',
      'Limited participation in social events',
      'Relationships based only on shared interests',
      'Easily influenced, or exploited due to naivety about social risks',
      'Avoids new experiences due to fear of unpredictability',
      'Relies on routines to manage daily activities',
      'Struggles without structure',
      'Neglects other activities to pursue interest',
    ],
  },
  {
    id: 'educational',
    title: 'Educational',
    indicators: [
      'Missing instructions that rely on social cues',
      'Anxiety about social situations affecting attendance',
      'Teachers reporting "not engaging," "not participating," or "off-task"',
      'Meltdowns or shutdowns triggered by social or sensory overload',
      'Avoidance of group work',
      'Difficulty following classroom interaction',
      'Distress in unstructured times (break/lunch)',
      'Reduced participation despite ability',
      'Behavioural incidents linked to overwhelm',
      'Difficulty with open-ended tasks or independent planning',
      'Interest interferes with schoolwork',
    ],
  },
  {
    id: 'home',
    title: 'Home',
    indicators: [
      'Rigid routines causing conflict or distress when disrupted',
      'Difficulty following multi-step instructions',
      'Emotional outbursts during transitions',
      'Family adapting daily life to avoid triggers',
      'Conflict around expectations',
      'Dependence on parents/carers beyond age expectations',
      'Resistance to changes in plans',
      'Family stress adapting to needs',
      'Interest interferes with responsibilities',
    ],
  },
  {
    id: 'emotional',
    title: 'Emotional',
    indicators: [
      'High anxiety around social situations or unpredictability',
      'Emotional exhaustion after school',
      'Shutdowns, meltdowns, withdrawal, or avoidance',
      'Low self-esteem from repeated social failures',
      'Distress when misunderstood or unable to express needs',
      'Experiences significant loneliness, anxiety, or depression related to social difficulties',
      'Anxiety about future (college, work, relationships) due to unpredictability',
      'Distressed when unable to access interest (e.g., during school, family events)',
    ],
  },
  {
    id: 'community',
    title: 'Community',
    indicators: [
      'Avoids clubs, sports, or group activities',
      'Overwhelm or distress in busy or noisy environments',
      'Difficulty following group rules or unspoken expectations',
      'Limited participation in age-typical leisure activities',
      'Needs 1:1 support to attend community settings',
      'Limited leisure interests outside solitary activities',
    ],
  },
  {
    id: 'selfCare',
    title: 'Self-care',
    indicators: [
      'Difficulty with sleep routines',
      'Challenges with washing, brushing teeth, or hair care',
      'Forgetting or avoiding hygiene tasks without prompts',
      'Distress around clothing textures or temperature changes',
      'Restricted eating linked to sensory issues',
      'Need for supervision beyond age expectations',
      'Interest interferes with sleep',
      'Forgets to eat/drink',
      'Overeats due to difficulty in sensing fullness',
    ],
  },
];

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

/**
 * All DSM-5 ASD criteria in assessment order.
 * All seven criteria (A1–A3, B1–B4) have full clinical content.
 * Criteria within each group stay together in the sidebar.
 */
export const SSI_CRITERIA = [
  CRITERION_A1,
  CRITERION_A2,
  CRITERION_A3,
  CRITERION_B1,
  CRITERION_B2,
  CRITERION_B3,
  CRITERION_B4,
];

/** Scoring guidance displayed at the top of each criterion group */
export const SCORING_NOTES = {
  A: 'Criteria A has three sub-criteria (A1, A2, A3). All three must be met.',
  B: 'At least two of four sub-criteria (B1–B4) must be met.',
};

export default SSI_CRITERIA;
